import { useEffect, useRef } from 'react';
import { useRunningStore } from '@/stores/useRunningStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { useAirQualityStore } from '@/stores/useAirQualityStore';
import { fetchAirQuality, fetchWeatherData } from '@/services/api/airQualityApi';

/**
 * Samples environmental data (AQI + weather + UV) during activity recording.
 * Re-fetches AQI based on GPS position when the runner moves >500m.
 * Caches weather for 5 minutes to save battery.
 */
export function useEnvironmentalSampling(isRecording: boolean, intervalMs = 30000) {
  const currentPosition = useLocationStore((s) => s.currentPosition);
  const addAQISample = useRunningStore((s) => s.addAQISample);
  const isPaused = useRunningStore((s) => s.recordingData.isPaused);
  const setAirQuality = useAirQualityStore((s) => s.setAirQuality);
  const weatherCache = useRef<{ data: any; timestamp: number } | null>(null);
  const aqiCache = useRef<{ data: any; lat: number; lng: number; timestamp: number } | null>(null);

  useEffect(() => {
    if (!isRecording) return;

    const sample = async () => {
      if (!currentPosition || isPaused) return;

      const { lat, lng } = currentPosition;

      // Re-fetch AQI if moved >500m from last fetch OR cache is >3 minutes old
      let aqiData = aqiCache.current;
      const shouldRefetchAqi = !aqiData
        || Date.now() - aqiData.timestamp > 180000
        || haversineDistance(lat, lng, aqiData.lat, aqiData.lng) > 500;

      if (shouldRefetchAqi) {
        const fresh = await fetchAirQuality(lat, lng);
        if (fresh) {
          aqiData = { data: fresh, lat, lng, timestamp: Date.now() };
          aqiCache.current = aqiData;
          // Also update the global store so live UI reflects changes
          setAirQuality(fresh);
        }
      }

      // Cache weather for 5 minutes
      let weather = weatherCache.current;
      if (!weather || Date.now() - weather.timestamp > 300000) {
        const fresh = await fetchWeatherData(lat, lng);
        if (fresh) {
          weather = { data: fresh, timestamp: Date.now() };
          weatherCache.current = weather;
        }
      }

      const aqi = aqiData?.data?.aqi ?? 0;
      const pm25 = aqiData?.data?.pm25?.value ?? null;

      addAQISample({
        timestamp: new Date().toISOString(),
        lat,
        lng,
        aqi,
        pm25: pm25 !== null ? Math.round(pm25 * 10) / 10 : null,
        temperature: weather?.data?.temperature ?? null,
        humidity: weather?.data?.humidity ?? null,
        uv_index: weather?.data?.uvIndex ?? null,
        wind_speed: weather?.data?.windSpeed ?? null,
        pressure: weather?.data?.pressure ?? null,
      });
    };

    sample();
    const interval = setInterval(sample, intervalMs);
    return () => clearInterval(interval);
  }, [isRecording, currentPosition, addAQISample, isPaused, intervalMs, setAirQuality]);
}

/** Haversine distance in meters between two lat/lng points */
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3;
  const toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
